import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { formatEmailOutput } from '../utils/emailFormatter';
import { sendEmailToUser } from '../services/api';

const EmailSharing = ({ email, offerSnapshot }) => {
  const [userEmail, setUserEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Load ConvertKit script
  useEffect(() => {
    // Only load the script if it hasn't been loaded already
    if (!document.querySelector('script[data-uid="4017c2ad17"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.setAttribute('data-uid', '4017c2ad17');
      script.src = 'https://matt.kit.com/4017c2ad17/index.js';
      document.head.appendChild(script);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }

    if (!email.subject) {
      setError('No email content to send. Please generate an email first.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const formattedOutput = formatEmailOutput(email, offerSnapshot);
      
      await sendEmailToUser(userEmail, formattedOutput);
      
      setIsSubmitted(true);
      setUserEmail('');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Email sending error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!email.subject) {
    return null;
  }

  if (isSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div>
            <h3 className="text-sm font-semibold text-green-900">
              📥 Your email newsletter will arrive within 15 minutes!
            </h3>
            <p className="text-xs text-green-700 mt-1">
              You're also now subscribed to the Creator Email Engine newsletter.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Mail className="w-5 h-5 text-primary-600" />
        <h3 className="text-base font-semibold text-gray-900">
          📬 Send Me My Newsletter
        </h3>
      </div>
      
      <p className="text-xs text-gray-600 mb-4">
        Get this email delivered to your inbox and join the Creator Email Engine newsletter for weekly writing tips.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="user-email" className="block text-xs font-medium text-gray-700 mb-1">
            Your Email Address
          </label>
          <input
            type="email"
            id="user-email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder="your@email.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            required
          />
        </div>

        {error && (
          <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !userEmail.trim()}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Mail className="w-3 h-3" />
              <span>Send Me This Email</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">
          💡 <strong>Pro tip:</strong> By requesting this email, you'll also get weekly writing insights and email templates delivered to your inbox.
        </p>
      </div>
    </div>
  );
};

export default EmailSharing; 